import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { signupSchema, type SignupFormData } from '@/schemas/auth';
import { useAuth } from '@/hooks/useAuth';
import { AuthLayout } from '@/layouts/AuthLayout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import type { ApiError } from '@/types/api';

export function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [apiError, setApiError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
  });

  const onSubmit = async (data: SignupFormData) => {
    setApiError('');
    try {
      await signup(data.orgName, data.email, data.password, data.firstName, data.lastName);
      navigate('/', { replace: true });
    } catch (err) {
      setApiError((err as ApiError).error || 'Registration failed');
    }
  };

  return (
    <AuthLayout title="Create Your Department" subtitle="Set up a new PPE Tracker account">
      {apiError && (
        <div role="alert" className="mb-4 rounded-md border border-accent-danger/30 bg-accent-danger/10 px-4 py-3 text-sm text-accent-danger">
          {apiError}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Input
          label="Department Name"
          error={errors.orgName?.message}
          {...register('orgName')}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="First Name"
            autoComplete="given-name"
            error={errors.firstName?.message}
            {...register('firstName')}
          />
          <Input
            label="Last Name"
            autoComplete="family-name"
            error={errors.lastName?.message}
            {...register('lastName')}
          />
        </div>
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          error={errors.email?.message}
          {...register('email')}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          error={errors.password?.message}
          helperText="Min 8 characters, 1 uppercase, 1 lowercase, 1 number"
          {...register('password')}
        />
        <Button type="submit" loading={isSubmitting} className="w-full">
          Create Account
        </Button>
      </form>

      <p className="mt-4 text-center text-sm text-text-muted">
        Already have an account?{' '}
        <Link to="/login" className="text-accent-primary hover:underline">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
